import {
  Table as CTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  HStack,
  Icon,
  Text,
  Center,
  Stack,
} from "@chakra-ui/react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import { useTable, Column } from "react-table";
import { DropDown } from "../DropDown/DropDown";
import { useState } from "react";

export interface TableProps {
  data: any;
  columns: Column[];
  centered?: boolean;
  paginationEnabled?: boolean; // Add paginationEnabled prop
  intialRowsPerPage?: number;
}

export const Table = ({
  columns,
  data,
  centered = false,
  paginationEnabled = true, // Default pagination to true
  intialRowsPerPage = 5,
}: TableProps) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data });

  const [rowsPerPage, setRowsPerPage] = useState(intialRowsPerPage);
  const [currentPage, setCurrentPage] = useState(1);

  const rowsPerPageOptions = [
    {
      id: 5,
      component: <Text>{5}</Text>,
    },
    {
      id: 10,
      component: <Text>{10}</Text>,
    },
    {
      id: 15,
      component: <Text>{15}</Text>,
    },
  ];

  const paginatedRows = paginationEnabled
    ? rows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
    : rows;

  return (
    <Box fontSize={{ base: "16px", lg: "18px" }}>
      <Box
        maxW="100%"
        overflowX="auto"
        overflowY="visible"
        position="relative"
        sx={{
          "&::-webkit-scrollbar": {
            display: "none",
          },
          "-ms-overflow-style": "none",
          "scrollbar-width": "none",
        }}
      >
        <CTable
          {...getTableProps()}
          bgColor="rgba(255, 255, 255, 0.05)"
          backdropFilter="blur(10px)"
          borderWidth={1}
          borderColor="rgba(255, 255, 255, 0.1)"
          borderRadius={4}
          sx={{
            borderBottomWidth: rows.length < rowsPerPage ? 0 : 1,
          }}
        >
          <Thead bgColor="rgba(118, 82, 247, 0.3)" borderRadius="12px">
            {headerGroups.map((headerGroup, index) => (
              <Tr
                {...headerGroup.getHeaderGroupProps()}
                key={index}
                height="50px"
              >
                {headerGroup.headers.map((column, index) => (
                  <Th
                    {...column.getHeaderProps()}
                    key={index}
                    color="white"
                    height="50px"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    <Center justifyContent={centered ? "center" : "flex-start"}>
                      <Text fontWeight="600" color="white" noOfLines={1}>
                        {column.render("Header")}
                      </Text>
                    </Center>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {paginatedRows.length > 0 ? (
              paginatedRows.map((row, index) => {
                prepareRow(row);
                return (
                  <Tr {...row.getRowProps()} key={index}>
                    {row.cells.map((cell, index) => (
                      <Td {...cell.getCellProps()} key={index} color="white">
                        {cell.render("Cell")}
                      </Td>
                    ))}
                  </Tr>
                );
              })
            ) : (
              <Tr>
                <Td
                  colSpan={columns.length}
                  textAlign="center"
                  py={32}
                  color="rgba(255, 255, 255, 0.6)"
                >
                  <Text fontSize="18px">Data not available</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </CTable>
      </Box>

      {paginationEnabled && (
        <Box
          backgroundColor="rgba(255, 255, 255, 0.05)"
          backdropFilter="blur(10px)"
          borderColor="rgba(255, 255, 255, 0.1)"
          borderWidth="1px"
          borderTopWidth="0px"
          borderBottomWidth={rows.length < rowsPerPage ? "0px" : "1px"}
          borderBottomRadius="20px"
          color="white"
        >
          <HStack justifyContent="space-between" p={3}>
            <HStack px={3}>
              <Text fontSize="14px" noOfLines={1}>
                Show rows per page
              </Text>
              <DropDown
                selected={{
                  id: rowsPerPage,
                  component: <Text>{rowsPerPage}</Text>,
                }}
                title={<></>}
                options={rowsPerPageOptions}
                onOptionChange={(id) => setRowsPerPage(id)}
              />
            </HStack>
            <Stack
              direction={{ base: "column", sm: "row" }}
              alignItems="center"
              gap={6}
            >
              <HStack>
                <Text>{`${(currentPage - 1) * rowsPerPage + 1}-${
                  currentPage * rowsPerPage < rows.length
                    ? currentPage * rowsPerPage
                    : rows.length
                }`}</Text>
                <Text textColor="gray.500">{` of ${rows.length}`}</Text>
              </HStack>
              <HStack gap={6} px={4}>
                <Icon
                  as={FaChevronLeft as React.ElementType}
                  onClick={() =>
                    setCurrentPage((page) => {
                      if (currentPage > 1) return page - 1;
                      return page;
                    })
                  }
                  color={currentPage > 1 ? "white" : "rgba(255, 255, 255, 0.3)"}
                  cursor={currentPage > 1 ? "pointer" : "default"}
                  _hover={currentPage > 1 ? { color: "brand.400" } : {}}
                />
                <Icon
                  as={FaChevronRight as React.ElementType}
                  onClick={() =>
                    setCurrentPage((page) => {
                      if (currentPage * rowsPerPage < rows.length)
                        return page + 1;
                      return page;
                    })
                  }
                  color={
                    currentPage * rowsPerPage < rows.length
                      ? "white"
                      : "rgba(255, 255, 255, 0.3)"
                  }
                  cursor={
                    currentPage * rowsPerPage < rows.length
                      ? "pointer"
                      : "default"
                  }
                  _hover={
                    currentPage * rowsPerPage < rows.length
                      ? { color: "brand.400" }
                      : {}
                  }
                />
              </HStack>
            </Stack>
          </HStack>
        </Box>
      )}
    </Box>
  );
};
